import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import ReactionSelector from '../components/ReactionSelector';
import ShareModal from '../components/ShareModal';
import {
  Mail, Briefcase, MapPin, User, Check, Clock, UserPlus, X, Phone,
  MessageSquare, Award, Sparkles, Plus, Trash2, Camera, Link as LinkIcon, Pencil, Save,
  ThumbsUp, PartyPopper, Heart, Lightbulb, Smile, Share2, Edit3, Terminal, Cpu, BarChart2, Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const RichPostContent = ({ id, content, media_url, media_type, event_date, location, celebration_type, mentions, allUsers, poll_question, poll_options, poll_results, user_poll_vote, onVote }) => {
  const { t } = useLanguage();
  if (!content && !media_url && !event_date && !celebration_type && !poll_question) return null;

  const parsedMentions = useMemo(() => {
    try {
      return mentions ? (typeof mentions === 'string' ? JSON.parse(mentions) : mentions) : [];
    } catch (e) { return []; }
  }, [mentions]);

  const parsedPollOptions = useMemo(() => {
    try {
      return poll_options ? (typeof poll_options === 'string' ? JSON.parse(poll_options) : poll_options) : [];
    } catch (e) { return []; }
  }, [poll_options]);

  const pollStats = useMemo(() => {
    if (!poll_results) return {};
    const stats = {};
    poll_results.split(',').forEach(item => {
      const [idx, count] = item.split(':');
      stats[idx] = parseInt(count);
    });
    return stats;
  }, [poll_results]);

  const totalVotes = Object.values(pollStats).reduce((a, b) => a + b, 0);

  const renderContentWithMentions = (text) => {
    if (!text && (!parsedMentions || parsedMentions.length === 0)) return null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {text && <p style={{ color: 'var(--text-body)', fontSize: '1rem', margin: 0, lineHeight: '1.6', fontWeight: 500 }}>{text}</p>}

        {parsedMentions && parsedMentions.length > 0 && (
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
            {parsedMentions.map(targetId => {
              const mentionedUser = allUsers?.find(u => Number(u.id) === Number(targetId));
              if (!mentionedUser) return null;
              return (
                <Link
                  key={targetId}
                  to={`/profile/${targetId}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: 'var(--accent-primary)',
                    padding: '4px 12px', fontSize: '0.75rem', fontWeight: 800,
                    borderRadius: '8px', border: '1px solid rgba(99, 102, 241, 0.2)',
                    textDecoration: 'none'
                  }}
                  className="hover-bright animate-fade"
                >
                  <div style={{ width: '18px', height: '18px', borderRadius: '5px', overflow: 'hidden', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {mentionedUser.avatar_url ? (
                      <img src={mentionedUser.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 900 }}>
                        {mentionedUser.username[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  @{mentionedUser.username}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderPoll = () => {
    if (!poll_question) return null;
    return (
      <div className="elite-glass-card" style={{
        margin: '1.25rem 0', padding: '1.5rem',
        borderRadius: '20px', border: '1px solid var(--glass-border)',
        background: 'rgba(255,255,255,0.01)', boxShadow: 'var(--shadow-inset)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.5rem' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '10px',
            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-cyan))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)'
          }}>
            <BarChart2 size={18} color="white" />
          </div>
          <h4 style={{ color: 'var(--text-heading)', fontSize: '1.05rem', fontWeight: 900, margin: 0, letterSpacing: '-0.01em' }}>{poll_question}</h4>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {parsedPollOptions.map((opt, i) => {
            const votes = pollStats[i] || 0;
            const percent = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
            const isVoted = user_poll_vote === i;

            return (
              <div
                key={i}
                onClick={() => onVote && onVote(id, i)}
                style={{
                  position: 'relative', height: '48px', borderRadius: '12px',
                  background: 'rgba(255,255,255,0.03)', border: `1px solid ${isVoted ? 'var(--accent-primary)' : 'var(--glass-border)'}`,
                  overflow: 'hidden', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className="hover-bright"
              >
                <div style={{
                  position: 'absolute', top: 0, left: 0, height: '100%',
                  width: `${percent}%`,
                  background: isVoted ? 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))' : 'rgba(99, 102, 241, 0.15)',
                  opacity: isVoted ? 0.2 : 0.1,
                  transition: 'width 0.8s cubic-bezier(0.19, 1, 0.22, 1)'
                }} />

                {isVoted && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: 'var(--accent-primary)', boxShadow: '0 0 10px var(--accent-primary)' }} />}

                <div style={{
                  position: 'relative', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'space-between', padding: '0 1.25rem',
                  fontSize: '0.9rem', fontWeight: isVoted ? 800 : 500, color: isVoted ? 'var(--accent-primary)' : 'var(--text-body)'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {isVoted && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-primary)' }} />}
                    {opt}
                  </span>
                  {totalVotes > 0 && <span style={{ opacity: 0.8, fontWeight: 900, fontFamily: 'monospace' }}>{percent}%</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '1.25rem', padding: '0.75rem 0 0', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
          </div>
        </div>
      </div>
    );
  };

  const renderCelebration = () => {
    if (!celebration_type) return null;
    const spotlightUser = parsedMentions?.length > 0 ? allUsers?.find(u => Number(u.id) === Number(parsedMentions[0])) : null;

    return (
      <div style={{
        margin: '1.5rem 0', padding: '2.5rem',
        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(99, 102, 241, 0.15))',
        borderRadius: '24px', border: '1px solid rgba(236, 72, 153, 0.25)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(236, 72, 153, 0.1)'
      }} className="celebration-card">
        <div style={{ position: 'relative', zIndex: 2 }}>
          <Award size={32} color="var(--accent-magenta)" style={{ margin: '0 auto 1.5rem', display: 'block' }} />
          <h4 style={{ color: 'var(--accent-magenta)', fontSize: '0.8rem', letterSpacing: '0.2em', fontWeight: 900, marginBottom: '1rem', textTransform: 'uppercase' }}>
            PROJECT RECOGNITION
          </h4>
          <h3 style={{
            color: 'var(--text-heading)', fontSize: '1.75rem', marginBottom: '1.5rem', fontWeight: 900,
            background: 'linear-gradient(to right, #fff, var(--accent-magenta))',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.02em'
          }}>
            {celebration_type.toUpperCase().replace('_', ' ')}
          </h3>
          {content && <p style={{ color: 'var(--text-body)', fontSize: '1.1rem', lineHeight: '1.6', fontWeight: 500, maxWidth: '500px', margin: '1rem auto 0' }}>{content}</p>}
        </div>
      </div>
    );
  };

  const renderEvent = () => {
    if (!event_date) return null;
    const dateObj = new Date(event_date);

    return (
      <div style={{
        margin: '1.5rem 0', background: 'rgba(255,255,255,0.01)', borderRadius: '24px', border: '1px solid var(--glass-border)',
        overflow: 'hidden', boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
      }}>
        <div style={{ padding: '1.5rem 2rem', background: 'linear-gradient(to right, rgba(99, 102, 241, 0.1), transparent)', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
              <Calendar size={20} />
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--text-heading)' }}>Strategic Mission</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--text-heading)', lineHeight: 1 }}>{dateObj.getDate()}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 800 }}>{dateObj.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</div>
          </div>
        </div>
        <div style={{ padding: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
            <Clock size={20} color="var(--accent-cyan)" />
            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--accent-cyan)' }}>{dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
          </div>
          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
            <MapPin size={20} color="var(--accent-primary)" />
            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-heading)' }}>{location || 'Remote'}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderMedia = () => {
    if (!media_url) return null;
    return (
      <div style={{ margin: '1.5rem 0', borderRadius: '24px', overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
        {media_type === 'video' ? <video src={media_url} controls style={{ width: '100%', display: 'block' }} /> : <img src={media_url} style={{ width: '100%', objectFit: 'cover', display: 'block' }} />}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {renderCelebration()}
      {!celebration_type && renderContentWithMentions(content)}
      {renderPoll()}
      {renderEvent()}
      {renderMedia()}
    </div>
  );
};


const ReactionSummary = ({ summary, count, commentCount, onReactionClick, onCommentClick }) => {
  if ((!summary || count === 0) && (!commentCount || commentCount === 0)) return null;
  const types = summary ? summary.split(',') : [];

  const getReactionStyle = (type) => {
    switch (type) {
      case 'celebrate': return { bg: '#eb4899', icon: <PartyPopper size={14} color="white" /> };
      case 'support': return { bg: '#f43f5e', icon: <Heart size={14} color="white" fill="white" style={{ transform: 'translateY(1px)' }} /> };
      case 'insightful': return { bg: '#f59e0b', icon: <Lightbulb size={14} color="white" /> };
      case 'funny': return { bg: '#0ea5e9', icon: <Smile size={14} color="white" /> };
      default: return { bg: '#3b82f6', icon: <ThumbsUp size={14} color="white" fill="white" style={{ transform: 'translateY(-1px)' }} /> };
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 1.25rem', borderTop: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.01)' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: count > 0 ? 'pointer' : 'default' }}
        onClick={() => count > 0 && onReactionClick && onReactionClick()}
      >
        {types.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {types.map((type, i) => {
              const { bg, icon } = getReactionStyle(type);
              return (
                <div key={type} style={{
                  marginLeft: i === 0 ? 0 : '-8px',
                  zIndex: 10 - i,
                  background: bg,
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--bg-card)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  flexShrink: 0
                }}>
                  {icon}
                </div>
              );
            })}
          </div>
        )}
        {count > 0 && (
          <span className="hover-underline" style={{ fontSize: '0.85rem', color: 'var(--text-heading)', fontWeight: 800, marginLeft: '4px' }}>{count}</span>
        )}
      </div>
      {commentCount > 0 && (
        <span
          className="hover-underline"
          onClick={() => onCommentClick && onCommentClick()}
          style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 500, cursor: 'pointer' }}
        >
          {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
        </span>
      )}
    </div>
  );
};

const EngagementModal = ({ isOpen, onClose, title, postId, type }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && postId) {
      setLoading(true);
      const endpoint = type === 'reactors' ? `/posts/${postId}/reactors` : `/posts/${postId}/commenters`;
      api.get(endpoint)
        .then(res => setUsers(res.data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, postId, type]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="glass-card animate-scale-up" style={{ width: '90%', maxWidth: '450px', maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: '24px' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{
            fontSize: '1.1rem',
            fontWeight: 900,
            background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>{title}</h3>
          <X size={20} color="var(--text-muted)" style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : users.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No engagement found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {users.map(user => (
                <div
                  key={user.id}
                  className="hover-item"
                  onClick={() => { navigate(`/profile/${user.id}`); onClose(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', borderRadius: '14px', cursor: 'pointer', background: 'rgba(255,255,255,0.02)' }}
                >
                  <div style={{ position: 'relative' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'var(--bg-deep)', overflow: 'hidden', flexShrink: 0 }}>
                      {user.avatar_url ? (
                        <img src={user.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--accent-primary)' }}>
                          {user.username[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    {type === 'reactors' && user.reaction_type && (
                      <div style={{
                        position: 'absolute',
                        bottom: '-4px',
                        right: '-4px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: user.reaction_type === 'celebrate' ? '#eb4899' : user.reaction_type === 'support' ? '#f43f5e' : user.reaction_type === 'insightful' ? '#f59e0b' : user.reaction_type === 'funny' ? '#0ea5e9' : '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid var(--bg-card)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        zIndex: 2
                      }}>
                        {user.reaction_type === 'celebrate' ? <PartyPopper size={10} color="white" /> :
                          user.reaction_type === 'support' ? <Heart size={10} color="white" fill="white" /> :
                            user.reaction_type === 'insightful' ? <Lightbulb size={10} color="white" /> :
                              user.reaction_type === 'funny' ? <Smile size={10} color="white" /> :
                                <ThumbsUp size={10} color="white" fill="white" />}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-heading)' }}>{user.full_name || user.username}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{user.role_title || 'NEO Member'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfirmDialog = ({ isOpen, onConfirm, onCancel, onSave, title, message }) => {
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)' }}>
      <div className="glass-card animate-scale-up" style={{ width: '90%', maxWidth: '400px', padding: '2rem', borderRadius: '24px', textAlign: 'center', border: '1px solid var(--accent-primary)' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 900, background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '1rem' }}>{title}</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: '1.5', marginBottom: '2rem' }}>{message}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button className="btn-cyber" onClick={onSave} style={{ width: '100%', padding: '0.8rem' }}>Save Changes</button>
          <button className="btn-cyber" onClick={onConfirm} style={{ width: '100%', padding: '0.8rem', background: 'transparent', color: 'var(--accent-magenta)', border: '1px solid rgba(244, 63, 94, 0.3)' }}>Discard</button>
          <button className="btn-cyber" onClick={onCancel} style={{ width: '100%', padding: '0.8rem', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--glass-border)' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const Modal = ({ title, isOpen, onClose, onSave, children, isDirty }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      setShowConfirm(false);
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);

  const handleCloseAttempt = () => {
    if (isDirty) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  };

  const handleDiscard = () => {
    setShowConfirm(false);
    onClose();
  };

  const handleSaveAndClose = async () => {
    await onSave();
    setShowConfirm(false);
  };

  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(5px)' }}>
      <div className="glass-card" style={{ width: '90%', maxWidth: '650px', maxHeight: '90vh', overflowY: 'auto', padding: '2.5rem', borderRadius: '24px', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 900,
            background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>{title}</h2>
          <button onClick={handleCloseAttempt} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={24} /></button>
        </div>
        {children}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--glass-border)' }}>
          <button className="btn-cyber" onClick={onSave} style={{ flex: 1 }}>Save Changes</button>
          <button className="btn-cyber" onClick={handleCloseAttempt} style={{ flex: 1, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--glass-border)' }}>Cancel</button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Unsaved Changes"
        message="Do you want to save your changes before leaving?"
        onConfirm={handleDiscard}
        onCancel={() => setShowConfirm(false)}
        onSave={handleSaveAndClose}
      />
    </div>
  );
};

function Profile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [isEditingIntro, setIsEditingIntro] = useState(false);
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [isEditingExp, setIsEditingExp] = useState(false);
  const [isEditingEdu, setIsEditingEdu] = useState(false);
  const [isEditingSkills, setIsEditingSkills] = useState(false);
  const [editData, setEditData] = useState({});
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hoveredPost, setHoveredPost] = useState(null);
  const [engagementModal, setEngagementModal] = useState({ isOpen: false, type: 'reactors', postId: null, title: '' });
  const [shareModal, setShareModal] = useState({ isOpen: false, post: null });
  const [activeComments, setActiveComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [editingComment, setEditingComment] = useState(null); // { id, content }
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('user'));

  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const viewRecorded = useRef(false);

  const isDirty = useMemo(() => {
    if (!profile) return false;
    // Compare essential fields
    return JSON.stringify(editData) !== JSON.stringify(profile);
  }, [editData, profile]);

  const handleCloseModals = () => {
    setEditData(profile);
    setIsEditingIntro(false);
    setIsEditingAbout(false);
    setIsEditingExp(false);
    setIsEditingEdu(false);
    setIsEditingSkills(false);
  };

  useEffect(() => {
    fetchProfile();
    recordProfileView();

    const linkedinToken = searchParams.get('linkedin_token');
    if (linkedinToken && id === 'me') {
      handleLinkedInSync(linkedinToken);
      // Remove token from URL
      searchParams.delete('linkedin_token');
      setSearchParams(searchParams);
    }
  }, [id]); // Removed searchParams to avoid double-fetch on param updates

  useEffect(() => {
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    try {
      const { data } = await api.get('/users/all');
      setAllUsers(data);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  useEffect(() => {
    const targetPostId = searchParams.get('post');
    if (targetPostId && userPosts.length > 0) {
      setTimeout(() => {
        const element = document.getElementById(`post-card-${targetPostId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.borderColor = 'var(--accent-primary)';
          element.style.boxShadow = '0 0 20px rgba(99, 102, 241, 0.2)';
          setTimeout(() => {
            element.style.borderColor = 'var(--glass-border)';
            element.style.boxShadow = 'none';
          }, 2000);
        }
      }, 500);
    }
  }, [userPosts, searchParams]);

  const recordProfileView = async () => {
    // 1. Basic self-view checks
    const isOwnProfile = id === 'me' ||
      Number(id) === Number(currentUser?.id) ||
      id === currentUser?.username;

    // 2. Session-based guard (prevents double-counting on re-mounts/back-nav)
    const sessionKey = `viewed_profile_${id}`;
    const alreadyViewedInSession = sessionStorage.getItem(sessionKey);

    console.log('[DEBUG] Analytics Engine:', {
      target: id,
      isOwn: isOwnProfile,
      sessionCached: !!alreadyViewedInSession
    });

    if (!isOwnProfile && !alreadyViewedInSession) {
      // Mark it immediately
      sessionStorage.setItem(sessionKey, 'true');
      try {
        console.log('[DEBUG] 🚀 Dispatching REAL view event for:', id);
        await api.post(`/users/record-view/${id}`);
      } catch (err) {
        console.error('Failed to record view');
        // If server failed, we could remove the item to allow retry,
        // but for analytics, usually we just let it be.
      }
    }
  };

  const fetchProfile = async () => {
    try {
      const { data } = id === 'me' ? await api.get('/users/me') : await api.get(`/users/${id}`);
      const parsedData = {
        ...data,
        skills: typeof data.skills === 'string' ? JSON.parse(data.skills || '[]') : (data.skills || []),
        experiences: typeof data.experiences === 'string' ? JSON.parse(data.experiences || '[]') : (data.experiences || []),
        education: typeof data.education === 'string' ? JSON.parse(data.education || '[]') : (data.education || [])
      };
      setProfile(parsedData);
      setEditData(parsedData);
      fetchUserPosts(data.id || (id === 'me' ? currentUser.id : id));

      if (id !== 'me' && data.id !== currentUser.id) {
        checkConnectionStatus(data.id);
      }
    } catch (err) {
      console.error('Failed to fetch profile');
    }
  };

  const fetchUserPosts = async (userId) => {
    try {
      const { data } = await api.get(`/posts/user/${userId}`);
      setUserPosts(data);
      recordImpressions(data);
    } catch (err) {
      console.error('Failed to fetch user posts');
    }
  };

  const recordImpressions = async (postsList) => {
    try {
      // Find posts we haven't seen in THIS browser session
      const unseenPostIds = postsList
        .filter(post => {
          const sessionKey = `viewed_post_${post.id}`;
          const isOwnPost = Number(post.user_id) === Number(currentUser?.id);
          return !isOwnPost && !sessionStorage.getItem(sessionKey);
        })
        .map(p => p.id);

      if (unseenPostIds.length > 0) {
        console.log(`[DEBUG] Recording ${unseenPostIds.length} unique post impressions...`);
        await api.post('/posts/record-impressions', { postIds: unseenPostIds });
        unseenPostIds.forEach(id => sessionStorage.setItem(`viewed_post_${id}`, 'true'));
      }
    } catch (err) {
      console.error('Failed to record impressions');
    }
  };

  const checkConnectionStatus = async (userId) => {
    try {
      const { data } = await api.get(`/connections/status/${userId}`);
      setConnectionStatus(data);
    } catch (err) {
      console.error('Failed to check connection status');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put('/users/me', editData);
      setProfile({ ...editData });
      setIsEditingIntro(false);
      setIsEditingAbout(false);
      setIsEditingExp(false);
      setIsEditingEdu(false);
      setIsEditingSkills(false);
    } catch (err) {
      console.error('Update failed');
    }
  };

  const addItem = (field, template) => {
    setEditData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), template]
    }));
  };

  const removeItem = (field, index) => {
    setEditData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateItem = (field, index, subfield, value) => {
    setEditData(prev => {
      const updatedList = [...(prev[field] || [])];
      updatedList[index] = { ...updatedList[index], [subfield]: value };
      return { ...prev, [field]: updatedList };
    });
  };

  const handleSummarize = async () => {
    setIsSummarizing(true);
    try {
      const { data } = await api.post(`/users/summarize/${profile.id}`);
      setProfile(prev => ({ ...prev, bio: data.summary }));
      setEditData(prev => ({ ...prev, bio: data.summary }));
    } catch (err) {
      console.error('AI summary failed');
    }
    setIsSummarizing(false);
  };

  const sendConnectionRequest = async () => {
    setLoadingAction(true);
    try {
      const { data } = await api.post('/connections/request', { receiver_id: profile.id });
      setConnectionStatus({ status: 'pending_sent', connectionId: data.connectionId });
    } catch (err) {
      alert('Failed to send request');
    }
    setLoadingAction(false);
  };

  const handleFileUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditData(prev => ({ ...prev, [field]: data.url }));
      // If intro modal isn't open, we might want to update profile directly too
      if (!isEditingIntro) {
        setProfile(prev => ({ ...prev, [field]: data.url }));
        await api.put('/users/me', { ...profile, [field]: data.url });
      }
    } catch (err) {
      alert('Upload failed');
    }
    setIsUploading(false);
  };

  const handleLinkedInSync = async (accessToken) => {
    try {
      const { data } = await api.post('/linkedin/sync', { accessToken });
      setProfile(prev => ({ ...prev, ...data.updates }));
      setEditData(prev => ({ ...prev, ...data.updates }));
      alert('Profile synchronized with LinkedIn profile data.');
    } catch (err) {
      alert('LinkedIn synchronization failed');
    }
  };

  const triggerLinkedInAuth = () => {
    // Window functionality deprecated as per user request for direct URL link
    alert('LinkedIn Sync has been deprecated. Please use the direct LinkedIn URL field in the Intro modal.');
  };


  const handleLike = async (postId, type = 'like') => {
    try {
      await api.post(`/social/posts/${postId}/like`, { type });
      // Fetch user posts again to update count
      fetchUserPosts(profile.id);
    } catch (err) { console.error('Reaction failed'); }
  };

  const handleVote = async (postId, optionIndex) => {
    try {
      await api.post(`/posts/${postId}/vote`, { optionIndex });
      fetchUserPosts(profile.id); // Refresh to show results
    } catch (err) {
      console.error('Failed to vote');
    }
  };

  const toggleComments = async (postId) => {
    if (activeComments[postId]) {
      setActiveComments(prev => ({ ...prev, [postId]: false }));
    } else {
      setActiveComments(prev => ({ ...prev, [postId]: true }));
      try {
        const { data } = await api.get(`/social/posts/${postId}/comments`);
        setPostComments(prev => ({ ...prev, [postId]: data }));
      } catch (err) { console.error('Failed to fetch comments'); }
    }
  };

  const handleComment = async (postId, content) => {
    if (!content.trim()) return;
    try {
      await api.post(`/social/posts/${postId}/comments`, { content });
      const { data } = await api.get(`/social/posts/${postId}/comments`);
      setPostComments(prev => ({ ...prev, [postId]: data }));
      fetchProfile(); // Refresh profile to update counts (especially activity)
    } catch (err) { console.error('Comment submission failed'); }
  };

  const handleUpdateComment = async (postId, commentId, content) => {
    if (!content.trim()) return;
    try {
      await api.put(`/social/comments/${commentId}`, { content });
      const { data } = await api.get(`/social/posts/${postId}/comments`);
      setPostComments(prev => ({ ...prev, [postId]: data }));
      setEditingComment(null);
    } catch (err) { console.error('Comment update failed'); }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (deletingCommentId !== commentId) {
      setDeletingCommentId(commentId);
      return;
    }

    try {
      await api.delete(`/social/comments/${commentId}`);
      const { data } = await api.get(`/social/posts/${postId}/comments`);
      setPostComments(prev => ({ ...prev, [postId]: data }));
      setDeletingCommentId(null);
      fetchProfile();
    } catch (err) {
      console.error('Comment deletion failed:', err);
    }
  };

  const getReactionIcon = (type) => {
    switch (type) {
      case 'celebrate': return <PartyPopper size={16} color="var(--accent-magenta)" />;
      case 'support': return <Heart size={16} color="#f43f5e" fill="#f43f5e" />;
      case 'insightful': return <Lightbulb size={16} color="#f59e0b" />;
      case 'funny': return <Smile size={16} color="#0ea5e9" />;
      case 'like': return <ThumbsUp size={16} color="var(--accent-primary)" />;
      default: return <ThumbsUp size={16} />;
    }
  };

  const getReactionLabel = (type) => {
    switch (type) {
      case 'celebrate': return 'Celebrate';
      case 'support': return 'Support';
      case 'insightful': return 'Insightful';
      case 'funny': return 'Funny';
      case 'like': return 'Like';
      default: return 'Like';
    }
  };

  const getReactionColor = (type) => {
    switch (type) {
      case 'celebrate': return 'var(--accent-magenta)';
      case 'support': return '#f43f5e';
      case 'insightful': return '#f59e0b';
      case 'funny': return '#0ea5e9';
      case 'like': return 'var(--accent-primary)';
      default: return 'var(--text-muted)';
    }
  };


  if (!profile) return <div style={{ paddingTop: '20%' }} className="text-center text-muted">Loading profile...</div>;

  const isMe = id === 'me' || profile.id === currentUser?.id;



  return (
    <>
      <div className="container animate-fade" style={{ maxWidth: '1000px', paddingBottom: '5rem' }}>
        {/* INTRO CARD */}
        <div className="elite-glass-card" style={{ padding: 0, overflow: 'hidden', marginBottom: '2.5rem' }}>
          <div style={{ height: '240px', backgroundImage: profile.banner_url ? `url(${profile.banner_url})` : 'linear-gradient(135deg, var(--bg-apex), var(--accent-primary))', backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.6))' }}></div>
            {isMe && (
              <>
                <input
                  type="file"
                  ref={bannerInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'banner_url')}
                />
                <button
                  onClick={() => bannerInputRef.current?.click()}
                  style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'var(--bg-surface)', color: 'var(--accent-primary)', border: 'none', borderRadius: '50%', padding: '10px', boxShadow: 'var(--shadow-apex)', cursor: 'pointer' }}
                  disabled={isUploading}
                >
                  <Camera size={20} />
                </button>
              </>
            )}
          </div>
          <div style={{ padding: '0 2.5rem 2.5rem', marginTop: '-75px', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ position: 'relative' }}>
                <div style={{ width: '150px', height: '150px', borderRadius: '50%', border: '5px solid var(--bg-card)', background: 'var(--bg-card)', overflow: 'hidden', boxShadow: 'var(--shadow-apex)' }}>
                  {profile.avatar_url ? <img src={profile.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Avatar" /> : <div style={{ width: '100%', height: '100%', background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><User size={60} color="var(--accent-primary)" /></div>}
                </div>
                {isMe && (
                  <>
                    <input
                      type="file"
                      ref={avatarInputRef}
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'avatar_url')}
                    />
                    <button
                      onClick={() => avatarInputRef.current?.click()}
                      style={{ position: 'absolute', bottom: '5px', right: '10px', background: 'var(--accent-primary)', color: 'var(--btn-text)', border: 'none', borderRadius: '50%', padding: '10px', boxShadow: 'var(--shadow-apex)', cursor: 'pointer' }}
                      disabled={isUploading}
                    >
                      <Camera size={16} />
                    </button>
                  </>
                )}
              </div>
              {isMe && <button onClick={() => setIsEditingIntro(true)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '10px' }}><Pencil size={20} /></button>}
            </div>
            <div style={{ marginTop: '2rem' }}>
              <h1 style={{ fontSize: '2.8rem', color: 'var(--text-heading)', fontWeight: 900, marginBottom: '0.25rem', letterSpacing: '-0.03em' }}>{profile.full_name || profile.username}</h1>
              <p style={{ fontSize: '1.2rem', color: 'var(--accent-primary)', fontWeight: 800, marginBottom: '0.75rem', letterSpacing: '0.02em' }}>{profile.role_title || t('professional_member')}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '0.95rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <MapPin size={14} color="var(--accent-magenta)" />
                  <span>{profile.availability || 'Remote'}</span>
                </div>
                <span style={{ opacity: 0.3 }}>•</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Mail size={14} color="var(--accent-cyan)" />
                  <span style={{ color: 'var(--text-body)', fontWeight: 600 }}>{profile.email}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
                {!isMe && (
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    {connectionStatus?.status === 'connected' ? (
                      <button className="btn-cyber" disabled style={{ background: 'rgba(34, 211, 238, 0.1)', color: 'var(--accent-cyan)', border: '1px solid var(--accent-cyan)' }}>
                        <Check size={16} /> {language === 'en' ? 'Connected' : 'Συνδεδεμένος'}
                      </button>
                    ) : connectionStatus?.status === 'pending_sent' ? (
                      <button className="btn-cyber" disabled style={{ background: 'rgba(99, 102, 241, 0.1)', color: 'var(--accent-primary)' }}>
                        <Clock size={16} /> {language === 'en' ? 'Pending' : 'Εκκρεμεί'}
                      </button>
                    ) : (
                      <button className="btn-cyber" onClick={sendConnectionRequest}>
                        {language === 'en' ? 'Connect' : 'Σύνδεση'}
                      </button>
                    )}
                    {(connectionStatus?.status === 'connected' || currentUser?.role === 'ap_admin') && (
                      <button onClick={() => navigate(`/messages?user=${profile.id}`)} className="btn-cyber">
                        {language === 'en' ? 'Message' : 'Μήνυμα'}
                      </button>
                    )}
                  </div>
                )}
                {profile.linkedin_url && (
                  <a href={profile.linkedin_url} target="_blank" rel="noreferrer" className="btn-cyber" style={{ background: 'rgba(56, 118, 191, 0.1)', color: 'var(--accent-secondary)', border: '1px solid #3876bf', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LinkIcon size={16} /> {t('linkedin_account')}
                  </a>
                )}
                {profile.cv_url && <a href={profile.cv_url} target="_blank" rel="noreferrer" className="btn-cyber" style={{ background: 'rgba(52, 211, 153, 0.1)', color: 'var(--accent-success)', border: '1px solid var(--accent-success)' }}>{t('download_cv')}</a>}
              </div>
            </div>
          </div>
        </div>

        {/* TWO COLUMN GRID */}
        <div className="profile-grid grid-to-stack" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 350px', gap: '2.5rem', alignItems: 'flex-start' }}>
          {/* LEFT COLUMN: PRIMARY NARRATIVE */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <div className="profile-narrative-tabs">
              <div
                className={`apex-tab-item ${!searchParams.get('tab') || searchParams.get('tab') === 'overview' ? 'active' : ''}`}
                onClick={() => setSearchParams({ tab: 'overview' })}
              >
                {language === 'en' ? 'Overview' : 'Επισκόπηση'}
              </div>
              <div
                className={`apex-tab-item ${searchParams.get('tab') === 'experience' ? 'active' : ''}`}
                onClick={() => setSearchParams({ tab: 'experience' })}
              >
                {t('profile_experience')}
              </div>
              <div
                className={`apex-tab-item ${searchParams.get('tab') === 'activity' ? 'active' : ''}`}
                onClick={() => setSearchParams({ tab: 'activity' })}
              >
                {t('profile_activity')}
              </div>
            </div>

            {(!searchParams.get('tab') || searchParams.get('tab') === 'overview') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div className="elite-glass-card" style={{ padding: '3.5rem' }}>
                  <h3 className="elite-section-header">
                    <User size={18} color="var(--accent-primary)" style={{ opacity: 0.8 }} /> {t('profile_about')}
                  </h3>
                  <p className="elite-text-body" style={{ fontSize: '1.1rem', whiteSpace: 'pre-line', lineHeight: '1.8' }}>
                    {profile.bio || t('bio_empty')}
                  </p>

                  {isMe && (
                    <div style={{ marginTop: '3rem', borderTop: '1px solid var(--elite-glass-border)', paddingTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '2rem' }}>
                      <button
                        onClick={handleSummarize}
                        disabled={isSummarizing}
                        style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', fontSize: '0.8rem', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.6rem', letterSpacing: '0.05em' }}
                      >
                        <Sparkles size={16} /> {isSummarizing ? t('synthesizing') : t('ai_enhance').toUpperCase()}
                      </button>
                      <button onClick={() => setIsEditingAbout(true)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', opacity: 0.6 }} className="hover-bright">
                        <Pencil size={18} />
                      </button>
                    </div>
                  )}
                </div>

                {/* EDUCATION CARD IN OVERVIEW */}
                <div className="elite-glass-card" style={{ padding: '3.5rem' }}>
                  <h3 className="elite-section-header">
                    <Award size={18} color="var(--accent-magenta)" style={{ opacity: 0.8 }} /> {t('profile_education')}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
                    {profile.education?.length > 0 ? profile.education.map((edu, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '2rem' }}>
                        <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(236, 72, 153, 0.05)', border: '1px solid rgba(236, 72, 153, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Award size={28} color="var(--accent-magenta)" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 className="elite-title-primary" style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>{edu.institution}</h4>
                          <p style={{ color: 'var(--text-body)', fontWeight: 700, fontSize: '1rem', marginBottom: '0.2rem' }}>{edu.degree}</p>
                          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>{edu.year}</p>
                        </div>
                      </div>
                    )) : <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.9rem' }}>{t('no_education')}</p>}
                  </div>
                  {isMe && (
                    <div style={{ marginTop: '3rem', borderTop: '1px solid var(--elite-glass-border)', paddingTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={() => setIsEditingEdu(true)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', opacity: 0.6 }} className="hover-bright">
                        <Pencil size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {searchParams.get('tab') === 'experience' && (
              <div className="elite-glass-card" style={{ padding: '3.5rem' }}>
                <h3 className="elite-section-header">
                  <Briefcase size={18} color="var(--accent-primary)" style={{ opacity: 0.8 }} /> {t('profile_experience')}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                  {profile.experiences?.length > 0 ? profile.experiences.map((exp, idx) => (
                    <div key={idx} className="elite-timeline-item">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div>
                          <h4 className="elite-title-primary" style={{ fontSize: '1.3rem' }}>{exp.title}</h4>
                          <p style={{ color: 'var(--accent-primary)', fontWeight: 800, fontSize: '1.05rem', marginTop: '4px' }}>{exp.company}</p>
                        </div>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{exp.period}</span>
                      </div>
                      <p className="elite-text-body" style={{ fontSize: '1rem' }}>{exp.description}</p>
                    </div>
                  )) : <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{t('no_experience')}</p>}
                </div>
                {isMe && (
                  <div style={{ marginTop: '3rem', borderTop: '1px solid var(--elite-glass-border)', paddingTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                     <button onClick={() => setIsEditingExp(true)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><Pencil size={20} /></button>
                  </div>
                )}
              </div>
            )}

            {searchParams.get('tab') === 'activity' && (
              <div className="elite-glass-card" style={{ padding: '3rem' }}>
                <h3 className="elite-section-header">
                  <Activity size={18} color="var(--accent-primary)" style={{ opacity: 0.8 }} /> {t('profile_activity')}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  {userPosts.length > 0 ? userPosts.map((post, idx) => (
                    <div key={idx} id={`post-card-tab-${post.id}`} className="elite-glass-card" style={{ padding: '2rem', background: 'rgba(255,255,255,0.01)' }}>
                      <RichPostContent {...post} allUsers={allUsers} onVote={handleVote} />
                      <ReactionSummary
                        summary={post.reaction_summary}
                        count={post.like_count}
                        commentCount={post.comment_count}
                        onReactionClick={() => setEngagementModal({ isOpen: true, type: 'reactors', postId: post.id, title: 'Reactions' })}
                        onCommentClick={() => toggleComments(post.id)}
                      />
                      <div style={{ display: 'flex', borderTop: '1px solid var(--elite-glass-border)', padding: '0.5rem 0 0 0', marginTop: '1rem' }}>
                        <button
                          className="hover-bright"
                          onClick={() => handleLike(post.id, post.user_reaction || 'like')}
                          onMouseEnter={() => setHoveredPost(post.id)}
                          onMouseLeave={() => setHoveredPost(null)}
                          style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            color: getReactionColor(post.user_reaction),
                            padding: '0.75rem',
                            fontSize: '0.8rem',
                            fontWeight: post.user_reaction ? 900 : 800,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            borderRadius: '12px',
                            transition: 'all 0.2s ease',
                            position: 'relative'
                          }}
                        >
                          {getReactionIcon(post.user_reaction)} {getReactionLabel(post.user_reaction)}
                          <ReactionSelector
                            isOpen={hoveredPost === post.id}
                            onSelect={(type) => handleLike(post.id, type)}
                            onClose={() => setHoveredPost(null)}
                          />
                        </button>
                        <button
                          onClick={() => toggleComments(post.id)}
                          className="hover-bright"
                          style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-muted)', padding: '0.75rem', fontSize: '0.8rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', borderRadius: '12px' }}
                        >
                          <MessageSquare size={18} /> {language === 'en' ? 'Comment' : 'Σχόλιο'}
                        </button>
                      </div>

                    {/* Comments Section */}
                    {activeComments[post.id] && (
                      <div style={{ padding: '0.75rem 1.5rem', background: 'rgba(255,255,255,0.01)', borderTop: '1px solid var(--glass-border)' }}>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
                          <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: 'var(--bg-deep)', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {currentUser?.avatar_url ? (
                              <img src={currentUser.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ fontSize: '0.6rem', color: 'var(--accent-primary)', fontWeight: 900 }}>
                                {(currentUser?.username || 'U')[0].toUpperCase()}
                              </div>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="Write a comment..."
                            style={{
                              flex: 1,
                              minWidth: 0,
                              background: 'rgba(255,255,255,0.03)',
                              border: '1px solid var(--glass-border)',
                              borderRadius: '999px',
                              color: 'var(--text-heading)',
                              padding: '0.4rem 1rem',
                              fontSize: '0.8rem',
                              outline: 'none',
                              transition: 'all 0.2s ease'
                            }}
                            className="hover-glow"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleComment(post.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {postComments[post.id]?.map(comment => (
                            <div key={comment.id} style={{ display: 'flex', gap: '0.5rem' }}>
                              <Link to={`/profile/${comment.user_id}`} style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid var(--glass-border)', overflow: 'hidden', flexShrink: 0 }}>
                                {comment.avatar_url ? <img src={comment.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'var(--accent-primary)', fontWeight: 900 }}>{comment.username[0].toUpperCase()}</div>}
                              </Link>
                              <div style={{ flex: 1, background: 'rgba(255,255,255,0.03)', padding: '0.5rem 0.75rem', borderRadius: '10px', border: editingComment?.id === comment.id ? '1px solid var(--accent-primary)' : '1px solid transparent' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.1rem', alignItems: 'center' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Link to={`/profile/${comment.user_id}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <span style={{ fontWeight: 800, fontSize: '0.75rem', color: 'var(--text-heading)' }}>{comment.full_name || comment.username}</span>
                                    </Link>
                                    <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>{new Date(comment.created_at).toLocaleDateString()}</span>
                                  </div>
                                  {(Number(currentUser?.id) === Number(comment.user_id) || currentUser?.role === 'ap_admin') && (
                                    <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                                      {deletingCommentId === comment.id ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(244, 63, 94, 0.1)', padding: '2px 8px', borderRadius: '4px', border: '1px solid rgba(244, 63, 94, 0.2)' }}>
                                          <span style={{ fontSize: '0.65rem', color: '#f43f5e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Confirm?</span>
                                          <Check size={14} color="#10b981" style={{ cursor: 'pointer' }} className="hover-bright" onClick={() => handleDeleteComment(post.id, comment.id)} />
                                          <X size={14} color="var(--text-muted)" style={{ cursor: 'pointer' }} className="hover-bright" onClick={() => setDeletingCommentId(null)} />
                                        </div>
                                      ) : editingComment?.id !== comment.id ? (
                                        <>
                                          {Number(currentUser?.id) === Number(comment.user_id) && (
                                            <Edit3 size={20} color="var(--text-muted)" style={{ cursor: 'pointer', padding: '4px' }} className="hover-bright" onClick={() => setEditingComment({ id: comment.id, content: comment.content })} />
                                          )}
                                          <Trash2 size={20} color="var(--text-muted)" style={{ cursor: 'pointer', padding: '4px' }} className="hover-bright" onClick={() => setDeletingCommentId(comment.id)} />
                                        </>
                                      ) : (
                                        <X size={20} color="var(--text-muted)" style={{ cursor: 'pointer', padding: '4px' }} onClick={() => setEditingComment(null)} />
                                      )}
                                    </div>
                                  )}
                                </div>
                                {editingComment?.id === comment.id ? (
                                  <div style={{ marginTop: '0.4rem' }}>
                                    <input
                                      autoFocus
                                      type="text"
                                      className="cyber-input"
                                      value={editingComment?.content || ''}
                                      onChange={(e) => setEditingComment({ ...editingComment, content: e.target.value })}
                                      style={{ width: '100%', padding: '0.3rem 0.6rem', fontSize: '0.75rem', marginBottom: '0.4rem' }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleUpdateComment(post.id, comment.id, editingComment.content);
                                        if (e.key === 'Escape') setEditingComment(null);
                                      }}
                                    />
                                    <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                      <button
                                        onClick={() => handleUpdateComment(post.id, comment.id, editingComment.content)}
                                        style={{ background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: '4px', padding: '0.15rem 0.5rem', fontSize: '0.65rem', cursor: 'pointer', fontWeight: 700 }}
                                      >
                                        SAVE
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <p style={{ fontSize: '0.8rem', color: 'var(--text-body)', lineHeight: '1.3' }}>{comment.content}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                )) : <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{t('no_posts')}</p>}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: SIDEBAR CONTENT */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* ANALYTICS CARD - ONLY VISIBLE TO OWNER */}
            {isMe && (
              <div className="elite-glass-card" style={{ padding: '2rem' }}>
                <h3 className="elite-section-header">
                  <BarChart2 size={18} color="var(--accent-cyan)" style={{ opacity: 0.8 }} /> {t('profile_analytics')}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                      <span style={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('profile_views')}</span>
                      <span style={{ color: 'var(--text-heading)', fontWeight: 900, fontSize: '1.25rem' }}>{profile?.profile_views || 0}</span>
                    </div>
                    <div style={{ height: '3px', background: 'rgba(255,255,255,0.05)', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min((profile?.profile_views || 0) / 10, 100)}%`, background: 'var(--accent-primary)', boxShadow: '0 0 10px var(--accent-primary)' }}></div>
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                      <span style={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('total_engagement')}</span>
                      <span style={{ color: 'var(--text-heading)', fontWeight: 900, fontSize: '1.25rem' }}>{typeof profile?.total_engagement === 'object' ? 0 : (profile?.total_engagement || 0)}</span>
                    </div>
                    <div style={{ height: '3px', background: 'rgba(255,255,255,0.05)', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min((profile?.total_engagement || 0) * 2, 100)}%`, background: 'var(--accent-cyan)', boxShadow: '0 0 10px var(--accent-cyan)' }}></div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '0.5rem' }}>
                    <div style={{ flex: 1, padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', textAlign: 'center' }}>
                      <div style={{ color: 'var(--text-heading)', fontWeight: 900, fontSize: '1.1rem' }}>{typeof profile?.engagement_breakdown?.likes === 'object' ? 0 : (profile?.engagement_breakdown?.likes || 0)}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase' }}>Likes</div>
                    </div>
                    <div style={{ flex: 1, padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', textAlign: 'center' }}>
                      <div style={{ color: 'var(--text-heading)', fontWeight: 900, fontSize: '1.1rem' }}>{typeof profile?.engagement_breakdown?.comments === 'object' ? 0 : (profile?.engagement_breakdown?.comments || 0)}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase' }}>Comments</div>
                    </div>
                  </div>
                </div>
              </div>
            )}



            {/* SKILLS CARD */}
            <div className="elite-glass-card" style={{ padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 className="elite-title-primary" style={{ fontSize: '0.9rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('profile_skills')}</h3>
                {isMe && <button onClick={() => setIsEditingSkills(true)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><Pencil size={18} /></button>}
              </div>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {profile.skills?.length > 0 ? profile.skills.map((skill, idx) => (
                  <span key={idx} style={{ background: 'rgba(99, 102, 241, 0.05)', color: 'var(--accent-primary)', padding: '0.5rem 1rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid rgba(99, 102, 241, 0.1)' }}>{skill}</span>
                )) : <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.8rem' }}>{t('no_skills')}</p>}
              </div>
            </div>

            {/* CONTACT CARD */}
            <div className="elite-glass-card" style={{ padding: '2rem' }}>
              <h3 className="elite-title-primary" style={{ fontSize: '0.9rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1.5rem' }}>{t('profile_contact')}</h3>
              {(isMe || connectionStatus?.status === 'connected' || currentUser?.role === 'ap_admin') ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: 'var(--text-body)', fontSize: '0.9rem' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(34, 211, 238, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Mail size={16} color="var(--accent-cyan)" />
                    </div>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>{profile.email}</span>
                  </div>
                  {profile.mobile_phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: 'var(--text-body)', fontSize: '0.9rem' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(99, 102, 241, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Phone size={16} color="var(--accent-primary)" />
                      </div>
                      <span style={{ fontWeight: 600 }}>{profile.mobile_phone}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: 'var(--text-body)', fontSize: '0.9rem' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(236, 72, 153, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <MapPin size={16} color="var(--accent-magenta)" />
                    </div>
                    <span style={{ fontWeight: 600 }}>{profile.availability || 'Remote'}</span>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '1.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px dashed var(--glass-border)' }}>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: '1.5' }}>{language === 'en' ? 'Contact details are restricted. Connect to view.' : 'Τα στοιχεία επικοινωνίας είναι περιορισμένα. Συνδεθείτε για προβολή.'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MODALS */}
      <Modal title="Edit Intro" isOpen={isEditingIntro} onClose={handleCloseModals} onSave={handleUpdate} isDirty={isDirty}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div className="input-group">
            <label>Full Name</label>
            <input className="cyber-input" value={editData.full_name || ''} onChange={e => setEditData({ ...editData, full_name: e.target.value })} />
          </div>
          <div className="input-group">
            <label>Headline / Role</label>
            <input className="cyber-input" value={editData.role_title || ''} onChange={e => setEditData({ ...editData, role_title: e.target.value })} />
          </div>
          <div className="input-group">
            <label>Location / Availability</label>
            <input className="cyber-input" value={editData.availability || ''} onChange={e => setEditData({ ...editData, availability: e.target.value })} />
          </div>
          <div className="grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div className="input-group">
              <label>Avatar URL</label>
              <input className="cyber-input" value={editData.avatar_url || ''} onChange={e => setEditData({ ...editData, avatar_url: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Banner URL</label>
              <input className="cyber-input" value={editData.banner_url || ''} onChange={e => setEditData({ ...editData, banner_url: e.target.value })} />
            </div>
          </div>
          <div className="grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div className="input-group">
              <label>CV / Dossier URL</label>
              <input className="cyber-input" value={editData.cv_url || ''} onChange={e => setEditData({ ...editData, cv_url: e.target.value })} />
            </div>
            <div className="input-group">
              <label>LinkedIn Profile URL</label>
              <input className="cyber-input" value={editData.linkedin_url || ''} placeholder="https://linkedin.com/in/..." onChange={e => setEditData({ ...editData, linkedin_url: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>

      <Modal title="Edit About" isOpen={isEditingAbout} onClose={handleCloseModals} onSave={handleUpdate} isDirty={isDirty}>
        <div className="input-group">
          <label>Summary</label>
          <textarea className="cyber-input" style={{ minHeight: '200px' }} value={editData.bio || ''} onChange={e => setEditData({ ...editData, bio: e.target.value })} />
        </div>
      </Modal>

      <Modal title="Edit Experience" isOpen={isEditingExp} onClose={handleCloseModals} onSave={handleUpdate} isDirty={isDirty}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {editData.experiences?.map((exp, i) => (
            <div key={i} style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <span style={{ color: 'var(--accent-primary)', fontWeight: 800 }}>Role #{i + 1}</span>
                <button onClick={() => removeItem('experiences', i)} style={{ background: 'transparent', border: 'none', color: 'var(--accent-magenta)', cursor: 'pointer' }}><Trash2 size={18} /></button>
              </div>
              <div className="input-group" style={{ marginBottom: '1.5rem' }}>
                <input className="cyber-input" placeholder="Role Title" value={exp.title} onChange={e => updateItem('experiences', i, 'title', e.target.value)} />
              </div>
              <div className="input-group" style={{ marginBottom: '1.5rem' }}>
                <input className="cyber-input" placeholder="Company" value={exp.company} onChange={e => updateItem('experiences', i, 'company', e.target.value)} />
              </div>
              <div className="input-group" style={{ marginBottom: '1.5rem' }}>
                <input className="cyber-input" placeholder="Time Frame" value={exp.period} onChange={e => updateItem('experiences', i, 'period', e.target.value)} />
              </div>
              <div className="input-group">
                <textarea className="cyber-input" placeholder="Description" value={exp.description} onChange={e => updateItem('experiences', i, 'description', e.target.value)} />
              </div>
            </div>
          ))}
          <button className="btn-cyber" style={{ background: 'transparent', color: 'var(--accent-primary)', border: '1px dashed var(--glass-border)' }} onClick={() => addItem('experiences', { title: '', company: '', period: '', description: '' })}><Plus size={18} /> Add Experience</button>
        </div>
      </Modal>
      <Modal title="Edit Education" isOpen={isEditingEdu} onClose={handleCloseModals} onSave={handleUpdate} isDirty={isDirty}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {editData.education?.map((edu, i) => (
            <div key={i} style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <span style={{ color: 'var(--accent-magenta)', fontWeight: 800 }}>Education #{i + 1}</span>
                <button onClick={() => removeItem('education', i)} style={{ background: 'transparent', border: 'none', color: 'var(--accent-magenta)', cursor: 'pointer' }}><Trash2 size={18} /></button>
              </div>
              <div className="input-group" style={{ marginBottom: '1.5rem' }}>
                <input className="cyber-input" placeholder="Institution" value={edu.institution} onChange={e => updateItem('education', i, 'institution', e.target.value)} />
              </div>
              <div className="input-group" style={{ marginBottom: '1.5rem' }}>
                <input className="cyber-input" placeholder="Degree" value={edu.degree} onChange={e => updateItem('education', i, 'degree', e.target.value)} />
              </div>
              <div className="input-group">
                <input className="cyber-input" placeholder="Year" value={edu.year} onChange={e => updateItem('education', i, 'year', e.target.value)} />
              </div>
            </div>
          ))}
          <button className="btn-cyber" style={{ background: 'transparent', color: 'var(--accent-primary)', border: '1px dashed var(--glass-border)' }} onClick={() => addItem('education', { degree: '', institution: '', year: '' })}><Plus size={18} /> Add Education</button>
        </div>
      </Modal>

      <Modal
        title={<span style={{ background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Edit Skills</span>}
        isOpen={isEditingSkills}
        onClose={handleCloseModals}
        onSave={handleUpdate}
        isDirty={isDirty}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div style={{ position: 'relative', display: 'flex', gap: '1rem', alignItems: 'center', padding: '1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '20px', border: '1px solid var(--glass-border)', boxShadow: 'var(--shadow-inset)' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                id="skill-input"
                className="cyber-input"
                placeholder="Add a skill..."
                style={{ width: '100%', paddingLeft: '1.25rem', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const val = e.target.value.trim();
                    if (val && !editData.skills.includes(val)) {
                      setEditData({ ...editData, skills: [...editData.skills, val] });
                      e.target.value = '';
                    }
                  }
                }}
              />
            </div>
            <button
              className="btn-cyber"
              style={{
                height: '54px',
                padding: '0 2rem',
                background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                border: 'none',
                boxShadow: '0 0 20px rgba(99, 102, 241, 0.3)',
                whiteSpace: 'nowrap'
              }}
              onClick={() => {
                const input = document.getElementById('skill-input');
                const val = input.value.trim();
                if (val && !editData.skills.includes(val)) {
                  setEditData({ ...editData, skills: [...editData.skills, val] });
                  input.value = '';
                }
              }}
            >
              Add
            </button>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.01)', borderRadius: '20px', padding: '1.5rem', border: '1px solid var(--glass-border)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 900, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Skills ({editData.skills?.length || 0})
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {editData.skills?.map((skill, idx) => (
                <div
                  key={idx}
                  className="hover-glow"
                  style={{
                    background: 'rgba(99, 102, 241, 0.08)',
                    color: 'var(--accent-primary)',
                    padding: '0.75rem 1.25rem',
                    borderRadius: '14px',
                    fontSize: '0.85rem',
                    fontWeight: 800,
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: 'var(--shadow-inset)'
                  }}
                >
                  {skill}
                  <X
                    size={16}
                    className="hover-bright"
                    style={{ cursor: 'pointer', opacity: 0.6, transition: 'all 0.2s ease' }}
                    onClick={() => setEditData({ ...editData, skills: editData.skills.filter((_, i) => i !== idx) })}
                  />
                </div>
              ))}
              {(!editData.skills || editData.skills.length === 0) && (
                <div style={{ width: '100%', textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.85rem', border: '1px dashed var(--glass-border)', borderRadius: '14px' }}>
                  No skills added yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        .input-group { display: flex; flex-direction: column; gap: 0.5rem; }
        .input-group label { font-size: 0.75rem; font-weight: 800; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
        .cyber-input { transition: all 0.3s ease; }
        .cyber-input:focus { border-color: var(--accent-primary); box-shadow: 0 0 15px rgba(99, 102, 241, 0.2); }
      `}</style>
      <EngagementModal
        {...engagementModal}
        onClose={() => setEngagementModal({ ...engagementModal, isOpen: false })}
      />
      <ShareModal
        {...shareModal}
        onClose={() => setShareModal({ ...shareModal, isOpen: false })}
      />
    </>
  );
}

export default Profile;
